import { alpha } from '@mui/material/styles';
import { COLORS } from './colors';

const PURE_WHITE = '#ffffff';
const PURE_BLACK = '#000000';

const SURFACE_FOCUS = '#1a1a1a';
const SURFACE_NORMAL = COLORS.background.paper;

const PRIMARY_ACCENT_FOCUS = COLORS.focusMode.text.accent;
const PRIMARY_ACCENT_NORMAL = COLORS.primary.main;

const WARNING_ACCENT_FOCUS = COLORS.semantic.warning.main;
const WARNING_ACCENT_DARK = '#e65100';

const SUCCESS_ACCENT_FOCUS = COLORS.semantic.success.main;
const SUCCESS_ACCENT_DARK = '#2e7d32';

const DISABLED_GRAY = COLORS.focusMode.text.disabled;

const createStateLayer = (
  focusColor: string,
  normalColor: string,
  focusOpacity: number,
  normalOpacity: number
) => ({
  focus: alpha(focusColor, focusOpacity),
  normal: alpha(normalColor, normalOpacity),
});

const createDepthShadow = (
  offsets: [string, string],
  focusOpacity: [number, number],
  normalOpacity: [number, number]
) => ({
  focus: `${offsets[0]} ${alpha(PURE_BLACK, focusOpacity[0])}, ${offsets[1]} ${alpha(PURE_BLACK, focusOpacity[1])}`,
  normal: `${offsets[0]} ${alpha(PURE_BLACK, normalOpacity[0])}, ${offsets[1]} ${alpha(PURE_BLACK, normalOpacity[1])}`,
});

const createAccentShadow = (focusColor: string, normalColor: string) => ({
  focus: `0 4px 12px ${alpha(focusColor, 0.4)}`,
  normal: `0 4px 12px ${alpha(normalColor, 0.3)}`,
});

const createGradient = (
  primaryColor: string,
  accentColor: string,
  warnColor: string,
  opacity: number
) =>
  `linear-gradient(45deg, ${alpha(primaryColor, opacity)}, ${alpha(accentColor, opacity)}, ${alpha(warnColor, opacity)})`;

/**
 * 背景色
 */
export const FOCUS_MODE_BACKGROUNDS = {
  primary: createStateLayer(SURFACE_FOCUS, SURFACE_NORMAL, 0.98, 0.98),
  darker: createStateLayer(SURFACE_FOCUS, SURFACE_NORMAL, 1, 1),
} as const;

/**
 * ボタン背景色
 */
export const FOCUS_MODE_BUTTON_BACKGROUNDS = {
  primary: createStateLayer(PRIMARY_ACCENT_FOCUS, PRIMARY_ACCENT_NORMAL, 0.2, 0.12),
  primaryLight: createStateLayer(PRIMARY_ACCENT_FOCUS, PRIMARY_ACCENT_NORMAL, 0.1, 0.06),
  primaryHover: createStateLayer(PRIMARY_ACCENT_FOCUS, PRIMARY_ACCENT_NORMAL, 0.3, 0.2),
  download: createStateLayer(WARNING_ACCENT_FOCUS, WARNING_ACCENT_DARK, 0.2, 0.12),
  downloadLight: createStateLayer(WARNING_ACCENT_FOCUS, WARNING_ACCENT_DARK, 0.1, 0.06),
  downloadHover: createStateLayer(WARNING_ACCENT_FOCUS, WARNING_ACCENT_DARK, 0.3, 0.2),
  scroll: createStateLayer(SUCCESS_ACCENT_FOCUS, SUCCESS_ACCENT_DARK, 0.2, 0.12),
  scrollLight: createStateLayer(SUCCESS_ACCENT_FOCUS, SUCCESS_ACCENT_DARK, 0.1, 0.06),
  scrollHover: createStateLayer(SUCCESS_ACCENT_FOCUS, SUCCESS_ACCENT_DARK, 0.3, 0.2),
  disabled: createStateLayer(PURE_WHITE, PURE_BLACK, 0.08, 0.04),
  disabledGray: createStateLayer(DISABLED_GRAY, PURE_BLACK, 0.1, 0.04),
} as const;

/**
 * ボーダー色
 */
export const FOCUS_MODE_BORDERS = {
  primary: createStateLayer(DISABLED_GRAY, PURE_BLACK, 0.3, 0.08),
  button: createStateLayer(PRIMARY_ACCENT_FOCUS, PRIMARY_ACCENT_NORMAL, 0.4, 0.2),
  buttonLight: createStateLayer(PRIMARY_ACCENT_FOCUS, PRIMARY_ACCENT_NORMAL, 0.2, 0.1),
  buttonHover: createStateLayer(PRIMARY_ACCENT_FOCUS, PRIMARY_ACCENT_NORMAL, 0.6, 0.4),
  download: createStateLayer(WARNING_ACCENT_FOCUS, WARNING_ACCENT_DARK, 0.4, 0.2),
  downloadLight: createStateLayer(WARNING_ACCENT_FOCUS, WARNING_ACCENT_DARK, 0.2, 0.1),
  downloadHover: createStateLayer(WARNING_ACCENT_FOCUS, WARNING_ACCENT_DARK, 0.6, 0.4),
  scroll: createStateLayer(SUCCESS_ACCENT_FOCUS, SUCCESS_ACCENT_DARK, 0.4, 0.2),
  scrollLight: createStateLayer(SUCCESS_ACCENT_FOCUS, SUCCESS_ACCENT_DARK, 0.2, 0.1),
  scrollHover: createStateLayer(SUCCESS_ACCENT_FOCUS, SUCCESS_ACCENT_DARK, 0.6, 0.4),
  disabled: createStateLayer(PURE_WHITE, PURE_BLACK, 0.12, 0.08),
  disabledGray: createStateLayer(DISABLED_GRAY, DISABLED_GRAY, 0.2, 0.2),
} as const;

/**
 * テキスト色
 */
export const FOCUS_MODE_COLORS = {
  primary: createStateLayer(PRIMARY_ACCENT_FOCUS, PRIMARY_ACCENT_NORMAL, 0.9, 0.9),
  primaryLight: createStateLayer(PRIMARY_ACCENT_FOCUS, PRIMARY_ACCENT_NORMAL, 0.6, 0.5),
  download: createStateLayer(WARNING_ACCENT_FOCUS, WARNING_ACCENT_DARK, 0.9, 0.9),
  downloadLight: createStateLayer(WARNING_ACCENT_FOCUS, WARNING_ACCENT_DARK, 0.6, 0.5),
  scroll: createStateLayer(SUCCESS_ACCENT_FOCUS, SUCCESS_ACCENT_DARK, 0.9, 0.9),
  scrollLight: createStateLayer(SUCCESS_ACCENT_FOCUS, SUCCESS_ACCENT_DARK, 0.6, 0.5),
  disabled: createStateLayer(PURE_WHITE, PURE_BLACK, 0.4, 0.3),
  disabledGray: createStateLayer(DISABLED_GRAY, PURE_BLACK, 0.4, 0.3),
  text: createStateLayer(PURE_WHITE, PURE_BLACK, 0.8, 0.6),
} as const;

/**
 * ボックスシャドウ
 */
export const FOCUS_MODE_SHADOWS = {
  primary: createDepthShadow(['0 12px 48px', '0 4px 16px'], [0.6, 0.4], [0.25, 0.15]),
  hover: createDepthShadow(['0 20px 64px', '0 8px 24px'], [0.8, 0.6], [0.3, 0.2]),
  button: createAccentShadow(PRIMARY_ACCENT_FOCUS, PRIMARY_ACCENT_NORMAL),
  download: createAccentShadow(WARNING_ACCENT_FOCUS, WARNING_ACCENT_DARK),
  scroll: createAccentShadow(SUCCESS_ACCENT_FOCUS, SUCCESS_ACCENT_DARK),
} as const;

/**
 * グラデーション
 */
export const FOCUS_MODE_GRADIENTS = {
  before: {
    focus: createGradient(
      PRIMARY_ACCENT_FOCUS,
      COLORS.accent.purple,
      COLORS.semantic.warning.main,
      0.2
    ),
    normal: createGradient(
      PRIMARY_ACCENT_NORMAL,
      COLORS.accent.purple,
      COLORS.semantic.warning.main,
      0.3
    ),
  },
} as const;

/**
 * フォーカスモードに応じた値を取得するヘルパー関数
 */
export function getFocusModeValue<
  T extends Record<string, { focus: string; normal: string }>,
  K extends keyof T,
>(styles: T, key: K, isFocusMode: boolean): string {
  return isFocusMode ? styles[key].focus : styles[key].normal;
}
